<template>
  <!-- Document Viewer Modal - Matching PurchaseRequestList styling -->
  <div v-if="isOpen" class="modal-backdrop" @click.self="close">
    <div class="modal-dialog" style="max-width: 1000px; height: 85vh;">
      <div class="modal-content" style="height: 100%; display: flex; flex-direction: column;">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ currentDocument?.documentType || 'Document' }}
            <span v-if="totalDocuments > 1" class="text-muted fw-normal ms-2">({{ currentIndex + 1 }} of {{ totalDocuments }})</span>
          </h5>
          <button @click="close" class="btn-close"></button>
        </div>

        <div class="modal-body p-4" style="flex: 1; overflow: auto; display: flex; align-items: center; justify-content: center; background-color: #525659;">
          <div v-if="isLoading" class="text-center">
            <div class="spinner-border text-light" role="status">
              <span class="sr-only">Loading document...</span>
            </div>
            <p class="mt-3 text-light">Loading document...</p>
          </div>

          <div v-else-if="isImage" class="document-preview-container">
            <img :src="documentUrl" :alt="currentDocument.documentType" class="preview-content">
          </div>

          <div v-else-if="isPdf" class="document-preview-container">
            <iframe :src="documentUrl" class="preview-content" title="PDF Preview"></iframe>
          </div>

          <div v-else class="preview-fallback">
            <p class="text-light mb-3">
              <strong>{{ getFileName(documentUrl) }}</strong>
            </p>
            <p class="text-light mb-4">
              Preview not available for this file type.
            </p>
            <div class="text-center">
              <a :href="documentUrl" target="_blank" class="btn btn-primary btn-sm px-3 me-2">
                <i class="fas fa-external-link-alt me-1"></i> Open in New Tab
              </a>
              <a :href="documentUrl" :download="getFileName(documentUrl)" class="btn btn-outline-light btn-sm px-3">
                <i class="fas fa-download me-1"></i> Download
              </a>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex gap-2 me-auto">
            <button 
              v-if="totalDocuments > 1"
              @click="previousDocument" 
              :disabled="currentIndex === 0"
              class="btn btn-outline-secondary btn-sm px-3">
              ← Previous
            </button>
            <button 
              v-if="totalDocuments > 1"
              @click="nextDocument" 
              :disabled="currentIndex === totalDocuments - 1"
              class="btn btn-outline-secondary btn-sm px-3">
              Next →
            </button>
          </div>
          <button @click="close" class="btn btn-secondary btn-sm px-3">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DocumentViewer',
  props: {
    documents: {
      type: Array,
      default: () => [],
      required: true
    },
    initialIndex: {
      type: Number,
      default: 0
    },
    urlServer: {
      type: String,
      default: process.env.VUE_APP_URL
    }
  },
  data() {
    return {
      isOpen: false,
      currentIndex: 0,
      isLoading: true
    }
  },
  computed: {
    currentDocument() {
      return this.documents[this.currentIndex] || {}
    },
    totalDocuments() {
      return this.documents.length
    },
    documentUrl() {
      const path = this.currentDocument?.documentPath
      return path ? `${this.urlServer}/files/${path}` : ''
    },
    isImage() {
      const ext = this.getFileExtension(this.documentUrl)
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext.toLowerCase())
    },
    isPdf() {
      const ext = this.getFileExtension(this.documentUrl)
      return ext.toLowerCase() === 'pdf'
    }
  },
  methods: {
    open(index = 0) {
      this.currentIndex = index
      this.isOpen = true
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
      }, 500)
    },
    close() {
      this.isOpen = false
    },
    nextDocument() {
      if (this.currentIndex < this.totalDocuments - 1) {
        this.currentIndex++
        this.isLoading = true
        setTimeout(() => {
          this.isLoading = false
        }, 300)
      }
    },
    previousDocument() {
      if (this.currentIndex > 0) {
        this.currentIndex--
        this.isLoading = true
        setTimeout(() => {
          this.isLoading = false
        }, 300)
      }
    },
    getFileExtension(url) {
      try {
        const path = url.split('?')[0]
        return path.split('.').pop()
      } catch {
        return ''
      }
    },
    getFileName(url) {
      try {
        const decoded = decodeURIComponent(url)
        return decoded.split('/').pop().split('?')[0]
      } catch {
        return 'Document'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* Modal Styling - Matching PurchaseRequestList.vue */

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 8px;
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-body {
  padding: 20px;
  
  .document-preview-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .preview-content {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  iframe.preview-content {
    width: 100%;
    height: 100%;
    border: none;
  }

  .preview-fallback {
    padding: 40px;
    text-align: center;

    p {
      margin: 0;
    }
  }
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  opacity: 0.5;
  
  &:hover {
    opacity: 1;
  }
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.text-muted {
  color: #6c757d !important;
}
</style>
